import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  FileText,
  Users,
  FolderKanban,
  LayoutDashboard,
  Radar,
  FileSearch,
  Calculator,
  BarChart3,
  Brain,
  Settings,
  Award,
  Swords,
  BookOpen,
  Building2,
  Palette,
  Key,
  Globe,
  TrendingUp,
  Target,
  Landmark,
  Trophy,
  Shield,
  CalendarDays,
  Activity,
  ArrowRight,
  Clock,
  CornerDownLeft,
} from 'lucide-react';
import { proposals as proposalsApi, people as peopleApi } from '../api';

type IconType = React.ComponentType<{ size?: number; 'aria-hidden'?: boolean }>;

interface CommandItem {
  id: string;
  label: string;
  category: 'Pages' | 'Actions' | 'Recent' | 'Proposals' | 'People';
  path?: string;
  action?: () => void;
  icon: IconType;
  subtitle?: string;
}

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ProposalLite {
  id: string;
  title?: string;
  client?: string;
  status?: string;
}

interface PersonLite {
  id: string;
  name?: string;
  role?: string;
  title?: string;
}

const STATIC_PAGES: CommandItem[] = [
  { id: 'dashboard', label: 'Dashboard', category: 'Pages', path: '/dashboard', icon: LayoutDashboard },
  { id: 'tenders', label: 'Tender Monitor', category: 'Pages', path: '/tenders', icon: Radar },
  { id: 'rfp-parser', label: 'RFP Parser', category: 'Pages', path: '/rfp-parser', icon: FileSearch },
  { id: 'go-no-go', label: 'Go/No-Go', category: 'Pages', path: '/go-no-go', icon: Target },
  { id: 'intelligence', label: 'Intelligence', category: 'Pages', path: '/intelligence', icon: Brain },
  { id: 'competitors', label: 'Competitors', category: 'Pages', path: '/competitors', icon: Swords },
  { id: 'clients', label: 'Clients', category: 'Pages', path: '/clients', icon: Building2 },
  { id: 'proposals', label: 'Proposals', category: 'Pages', path: '/proposals', icon: FileText },
  { id: 'content-library', label: 'Content Library', category: 'Pages', path: '/content-library', icon: BookOpen },
  { id: 'templates', label: 'Templates', category: 'Pages', path: '/templates', icon: FileText },
  { id: 'fee-estimator', label: 'Fee Estimator', category: 'Pages', path: '/fee-estimator', icon: Calculator },
  { id: 'nz-govt-wizard', label: 'NZ Govt Wizard', category: 'Pages', path: '/nz-govt-wizard', icon: Landmark },
  { id: 'people', label: 'People', category: 'Pages', path: '/people', icon: Users },
  { id: 'projects', label: 'Projects', category: 'Pages', path: '/projects', icon: FolderKanban },
  { id: 'resource-planner', label: 'Resource Planner', category: 'Pages', path: '/resource-planner', icon: CalendarDays },
  { id: 'certifications', label: 'Certifications', category: 'Pages', path: '/certifications', icon: Award },
  { id: 'analytics', label: 'Analytics', category: 'Pages', path: '/analytics', icon: BarChart3 },
  { id: 'advanced-analytics', label: 'Advanced Analytics', category: 'Pages', path: '/advanced-analytics', icon: TrendingUp },
  { id: 'lessons', label: 'Lessons', category: 'Pages', path: '/lessons', icon: BookOpen },
  { id: 'audit-log', label: 'Audit Trail', category: 'Pages', path: '/audit-log', icon: Shield },
  // Achievements hidden — gamification feels out of place for engineering
  // consultancies pre-pilot. Route still works for direct URL access.
  // Re-enable here when the achievements feature has more substance.
  // { id: 'achievements', label: 'Achievements', category: 'Pages', path: '/achievements', icon: Trophy },
  { id: 'ai-usage', label: 'AI Usage', category: 'Pages', path: '/ai-usage', icon: Activity },
  { id: 'settings', label: 'Settings', category: 'Pages', path: '/settings', icon: Settings },
  { id: 'branding', label: 'Branding', category: 'Pages', path: '/settings/branding', icon: Palette },
  { id: 'api-settings', label: 'API & Webhooks', category: 'Pages', path: '/settings/api', icon: Key },
  { id: 'tender-sources', label: 'Tender Sources', category: 'Pages', path: '/settings/tender-sources', icon: Globe },
];

const RECENT_KEY = 'bidforge-command-palette-recent';

function fuzzyMatch(query: string, text: string): number[] | null {
  if (!query) return [];
  const lowerQuery = query.toLowerCase();
  const lowerText = text.toLowerCase();
  const indices: number[] = [];
  let qi = 0;

  for (let ti = 0; ti < lowerText.length && qi < lowerQuery.length; ti++) {
    if (lowerText[ti] === lowerQuery[qi]) {
      indices.push(ti);
      qi++;
    }
  }

  return qi === lowerQuery.length ? indices : null;
}

function HighlightedText({ text, indices }: { text: string; indices: number[] }) {
  if (!indices.length) return <span>{text}</span>;
  const indexSet = new Set(indices);
  return (
    <span>
      {text.split('').map((char, i) => (
        indexSet.has(i) ? (
          <mark key={i} className="cmdk-highlight">{char}</mark>
        ) : (
          <span key={i}>{char}</span>
        )
      ))}
    </span>
  );
}

export default function CommandPalette({ isOpen, onClose }: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [proposals, setProposals] = useState<ProposalLite[]>([]);
  const [people, setPeople] = useState<PersonLite[]>([]);
  const [loadedRemote, setLoadedRemote] = useState(false);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  // Lock body scroll while open
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  // Restore focus to whatever was focused before opening
  useEffect(() => {
    if (isOpen) {
      previouslyFocused.current = document.activeElement as HTMLElement | null;
    } else if (previouslyFocused.current) {
      previouslyFocused.current.focus?.();
    }
  }, [isOpen]);

  // Fetch proposals + people once on first open
  useEffect(() => {
    if (!isOpen || loadedRemote) return;
    let cancelled = false;
    Promise.allSettled([proposalsApi.list(), peopleApi.list()]).then(([propRes, peopleRes]) => {
      if (cancelled) return;
      if (propRes.status === 'fulfilled') {
        const data = propRes.value?.data;
        const list = Array.isArray(data) ? data : (data?.items || data?.results || []);
        setProposals(list as ProposalLite[]);
      }
      if (peopleRes.status === 'fulfilled') {
        const data = peopleRes.value?.data;
        const list = Array.isArray(data) ? data : (data?.items || data?.results || []);
        setPeople(list as PersonLite[]);
      }
      setLoadedRemote(true);
    });
    return () => {
      cancelled = true;
    };
  }, [isOpen, loadedRemote]);

  const getRecentItems = useCallback((): CommandItem[] => {
    try {
      const stored = localStorage.getItem(RECENT_KEY);
      if (!stored) return [];
      const ids: string[] = JSON.parse(stored);
      const items: CommandItem[] = [];
      for (const id of ids) {
        const page = STATIC_PAGES.find(p => p.id === id);
        if (page) items.push({ ...page, category: 'Recent' });
      }
      return items;
    } catch {
      return [];
    }
  }, []);

  const saveRecent = useCallback((id: string) => {
    try {
      const stored = localStorage.getItem(RECENT_KEY);
      let ids: string[] = stored ? JSON.parse(stored) : [];
      ids = [id, ...ids.filter(i => i !== id)].slice(0, 5);
      localStorage.setItem(RECENT_KEY, JSON.stringify(ids));
    } catch {
      // ignore storage errors
    }
  }, []);

  const actionItems: CommandItem[] = useMemo(() => [
    {
      id: 'action-new-proposal',
      label: 'New Proposal',
      category: 'Actions',
      action: () => navigate('/proposals'),
      icon: FileText,
    },
    {
      id: 'action-fee-estimate',
      label: 'New Fee Estimate',
      category: 'Actions',
      action: () => navigate('/fee-estimator'),
      icon: Calculator,
    },
    {
      id: 'action-parse-rfp',
      label: 'Parse an RFP',
      category: 'Actions',
      action: () => navigate('/rfp-parser'),
      icon: FileSearch,
    },
  ], [navigate]);

  const proposalItems: CommandItem[] = useMemo(() =>
    proposals.map(p => ({
      id: `proposal-${p.id}`,
      label: p.title || 'Untitled Proposal',
      category: 'Proposals' as const,
      path: `/proposals/${p.id}/build`,
      icon: FileText,
      subtitle: p.client || p.status,
    })),
    [proposals]
  );

  const peopleItems: CommandItem[] = useMemo(() =>
    people.map(p => ({
      id: `person-${p.id}`,
      label: p.name || 'Unnamed Person',
      category: 'People' as const,
      path: `/people/${p.id}`,
      icon: Users,
      subtitle: p.role || p.title,
    })),
    [people]
  );

  const recentItems = useMemo(() => (isOpen ? getRecentItems() : []), [isOpen, getRecentItems]);

  const allItems = useMemo(
    () => [...recentItems, ...actionItems, ...STATIC_PAGES, ...proposalItems, ...peopleItems],
    [recentItems, actionItems, proposalItems, peopleItems]
  );

  const filteredItems = useMemo(() => {
    const q = query.trim();
    if (!q) {
      return allItems.map(item => ({ item, indices: [] as number[] }));
    }
    return allItems
      .map(item => {
        const indices = fuzzyMatch(q, item.label);
        if (indices) return { item, indices };
        // Also match subtitle (client/role)
        if (item.subtitle && fuzzyMatch(q, item.subtitle)) {
          return { item, indices: [] };
        }
        return null;
      })
      .filter((r): r is { item: CommandItem; indices: number[] } => r !== null);
  }, [query, allItems]);

  // Deduplicate by id (Recent items duplicate Pages)
  const uniqueResults = useMemo(() => {
    const seen = new Set<string>();
    return filteredItems.filter(({ item }) => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  }, [filteredItems]);

  const grouped = useMemo(() => ({
    Recent: uniqueResults.filter(r => r.item.category === 'Recent'),
    Actions: uniqueResults.filter(r => r.item.category === 'Actions'),
    Pages: uniqueResults.filter(r => r.item.category === 'Pages'),
    Proposals: uniqueResults.filter(r => r.item.category === 'Proposals').slice(0, 8),
    People: uniqueResults.filter(r => r.item.category === 'People').slice(0, 8),
  }), [uniqueResults]);

  const flatResults = useMemo(
    () => [...grouped.Recent, ...grouped.Actions, ...grouped.Pages, ...grouped.Proposals, ...grouped.People],
    [grouped]
  );

  // Reset query + selection on open
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  // Reset selection when filter changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current) {
      const selected = listRef.current.querySelector('[data-selected="true"]');
      if (selected) {
        selected.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex, flatResults.length]);

  const handleSelect = useCallback((item: CommandItem) => {
    // Only persist 'page' navigations as recent (not dynamic proposal/person ids)
    if (item.category === 'Pages' || item.category === 'Recent') {
      saveRecent(item.id);
    }
    if (item.action) {
      item.action();
    } else if (item.path) {
      navigate(item.path);
    }
    onClose();
  }, [navigate, onClose, saveRecent]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(i => Math.min(i + 1, Math.max(flatResults.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const result = flatResults[selectedIndex];
      if (result) handleSelect(result.item);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  }, [flatResults, selectedIndex, handleSelect, onClose]);

  if (!isOpen) return null;

  const renderGroup = (label: string, items: typeof flatResults) => {
    if (items.length === 0) return null;
    return (
      <div className="cmdk-group" key={label}>
        <div className="cmdk-group-label">{label}</div>
        {items.map((result) => {
          const globalIndex = flatResults.indexOf(result);
          const isSelected = globalIndex === selectedIndex;
          const Icon = result.item.icon;
          return (
            <button
              type="button"
              key={result.item.id}
              data-selected={isSelected}
              className={`cmdk-item${isSelected ? ' cmdk-item-selected' : ''}`}
              onClick={() => handleSelect(result.item)}
              onMouseMove={() => {
                if (!isSelected) setSelectedIndex(globalIndex);
              }}
            >
              <Icon size={16} aria-hidden />
              <span className="cmdk-item-label">
                <HighlightedText text={result.item.label} indices={result.indices} />
                {result.item.subtitle && (
                  <span className="cmdk-item-subtitle"> · {result.item.subtitle}</span>
                )}
              </span>
              {isSelected && <CornerDownLeft size={13} aria-hidden className="cmdk-item-enter" />}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div
      className="cmdk-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="presentation"
    >
      <div
        ref={dialogRef}
        className="cmdk-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onKeyDown={handleKeyDown}
      >
        <div className="cmdk-input-wrap">
          <Search size={16} aria-hidden className="cmdk-input-icon" />
          <input
            ref={inputRef}
            type="text"
            className="cmdk-input"
            placeholder="Search pages, proposals, people..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search commands"
            aria-autocomplete="list"
            aria-controls="cmdk-list"
            aria-activedescendant={flatResults[selectedIndex] ? `cmdk-item-${flatResults[selectedIndex].item.id}` : undefined}
          />
        </div>
        <div ref={listRef} className="cmdk-list" id="cmdk-list" role="listbox">
          {flatResults.length === 0 ? (
            <div className="cmdk-empty">
              No results for "{query}"
            </div>
          ) : (
            <>
              {renderGroup('Recent', grouped.Recent)}
              {renderGroup('Actions', grouped.Actions)}
              {renderGroup('Pages', grouped.Pages)}
              {renderGroup('Proposals', grouped.Proposals)}
              {renderGroup('People', grouped.People)}
            </>
          )}
        </div>
        <div className="cmdk-footer">
          <span><kbd className="cmdk-kbd">&uarr;&darr;</kbd> navigate</span>
          <span><kbd className="cmdk-kbd">&crarr;</kbd> select</span>
          <span><kbd className="cmdk-kbd">esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
}
